import React, { useState, useEffect } from 'react';
import { Link } from 'gatsby';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { displayHidden } from '../Menu/index';
import { BoxInline, Box, Flex } from '../../../style/basicStyle';
import MenuMobile from '../MenuMobile';
import feather from '../../../utils/feather';
import { useMobileMenu } from '../MobileMenuContext';
import { disableBrowserStyling, displayInline, bulldogsColor1, bulldogsColor2 } from '../../../style/genericStyling';

const featherParentLink = css`
  display: inline;
  transition: all 0.5s ease-in-out;
`;

const featherLink = css`
  transition: all 0.5s ease-in-out;
`;

const expandChild = css`
  position: absolute;
  overflow: visible;
  padding-top: 4px;
  padding-left: 10px;
  transition: all 0.5s ease-in-out;
`;

const menuIconAnimation = css`
  transform: rotate(90deg);
  transition: transform 0.3s ease;
`;

const pulse = css`
  animation: pulse 0.6s ease-in-out;
  @keyframes pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }
`;

// eslint-disable-next-line react/prop-types
const NavBarLinkMobile = ({ linkName, styling, childLinkStyling, link, navDarkActive, hidden, showExperimental, childLinks, toggleNav, requireAuth, requireTuition, requireTournaments, roleRequired, requireOrders, requireEvents, requireAdmin, authUser, hideWithAuth, linkNameSignedIn }) => {
  // Generate a unique ID for this menu item
  const menuId = `menu-${linkName.replace(/\s+/g, '-').toLowerCase()}`;
  
  // Use our mobile menu context to coordinate open/closed states
  const { openMenuId, toggleMenu } = useMobileMenu();
  
  // Local state to handle animation timing
  const [visible, setVisible] = useState(false); 
  const [closing, setClosing] = useState(false);
  
  // Update local state based on context
  useEffect(() => {
    const isOpen = openMenuId === menuId;
    if (isOpen !== visible) {
      if (isOpen) {
        setVisible(true);
        setClosing(false);
      } else if (visible) {
        // If menu is currently visible but should be closed
        setClosing(true);
        // Wait for animation to finish before hiding completely
        setTimeout(() => {
          setVisible(false);
          setClosing(false);
        }, 300); // This should match the animation duration
      }
    }
  }, [openMenuId, menuId, visible]);

  const menuToggle = () => {
    if (childLinks && childLinks.length && childLinks.length > 0) {
      // Use the context toggle which will handle closing other menus
      toggleMenu(menuId);
    } else {
      toggleNav();
    }
  };
  
  // eslint-disable-next-line react/prop-types
  const username = authUser && linkNameSignedIn && authUser.username && authUser.username.length && authUser.username.split(' ')[0];
  
  return (
    (!requireAuth || (authUser && !hideWithAuth)) && (
      <li css={[ hidden && displayHidden]}>
        <Box
          css={[
            disableBrowserStyling,
            visible && pulse
          ]}
          my={[2]}
          fontSize={[4]}
          p={[1]}
          style={{ fontWeight: 'bold', cursor: 'pointer' }}
          onClick={() => menuToggle()}
          role="button"
          tabIndex="-1"
          onKeyPress={() => menuToggle()}
        >
          {(childLinks && childLinks.length > 0 && (
            <Box my={[0]}>
              {/* eslint-disable-next-line react/prop-types */}
              <BoxInline>{(username && username.length && username) || linkName}</BoxInline>
              <BoxInline css={[expandChild, visible && menuIconAnimation]}>
                {(!visible && feather('plus', ['25', '25'], featherLink, featherParentLink)) || 
                 feather('minus', ['25', '25'], featherLink, featherParentLink)}
              </BoxInline>
            </Box>
          )) || <Link to={link}>{linkName}</Link>}
        </Box>
        {childLinks && childLinks.length > 0 && (
          <MenuMobile
            linkName={linkName}
            hidden={hidden}
            childLinkStyling={childLinkStyling}
            linkNameSignedIn={linkNameSignedIn}
            hideWithAuth={hideWithAuth}
            requireAdmin={requireAdmin}
            requireOrders={requireOrders}
            requireTournaments={requireTournaments}
            requireTuition={requireTuition}
            requireEvents={requireEvents}
            requireAuth={requireAuth}
            roleRequired={roleRequired}
            authUser={authUser}
            navDarkActive={navDarkActive}
            toggleNav={toggleNav}
            childLinks={childLinks}
            isVisible={visible}
            closing={closing}
          />
        )}
      </li>
    )
  );
};

export default NavBarLinkMobile;

NavBarLinkMobile.propTypes = {
  toggleNav: PropTypes.func.isRequired,
  linkName: PropTypes.string.isRequired,
  link: PropTypes.string,
  showExperimental: PropTypes.bool,
  childLinks: PropTypes.arrayOf(
    PropTypes.shape({
      linkName: PropTypes.string,
      link: PropTypes.string,
      categoryName: PropTypes.string
    })
  )
};

NavBarLinkMobile.defaultProps = {
  link: '/',
  showExperimental: false,
  childLinks: []
};
